import React, { useMemo, useState } from 'react';
import { Teacher } from '../types';
import ApiService from '../services/api';
import { useClassroomData, processedClassroomData } from '../data/classroomData';
import './SmartcareSignInButton.css';

interface SmartcareSignInButtonProps {
  teacher: Teacher;
  currentClassroomId?: string;
  onSignInComplete?: () => void;
}

const SmartcareSignInButton: React.FC<SmartcareSignInButtonProps> = ({ 
  teacher, 
  currentClassroomId, 
  onSignInComplete 
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [selectedClassroom, setSelectedClassroom] = useState(currentClassroomId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Access classrooms data from query and processing function
  const classroomData = useClassroomData().data;
  const { classroomOptions, sections, specialCategories } = useMemo(()=> processedClassroomData(classroomData), [classroomData]);
  
  // Prepare a flat list of all classrooms for the dropdown
  const availableClassrooms = React.useMemo(() => {
    const allClassrooms: {id: string, name: string}[] = [];
    
    // Add regular classrooms with section names
    if (sections && sections.length > 0) {
      sections.forEach(section => {
        section.classrooms.forEach(classroom => {
          allClassrooms.push({
            id: classroom.id,
            name: `${classroom.name} (${section.name})`
          });
        });
      });
    } else {
      // Fallback to the simpler classroomOptions if sections aren't available
      classroomOptions.forEach(classroom => {
        allClassrooms.push({
          id: classroom.id,
          name: classroom.name
        });
      });
    }
    
    // Add special classrooms (office, unassigned)
    if (specialCategories && specialCategories.length > 0) {
      specialCategories.forEach(classroom => {
        allClassrooms.push({
          id: classroom.id,
          name: classroom.name
        });
      });
    }
    
    return allClassrooms.sort((a, b) => a.name.localeCompare(b.name));
  }, [sections, specialCategories, classroomOptions]);

  const handleOpenDialog = () => {
    setSelectedClassroom(currentClassroomId || (availableClassrooms.length > 0 ? availableClassrooms[0].id : ''));
    setShowDialog(true);
    setError('');
  };

  const handleSignIn = async () => {
    if (!selectedClassroom) {
      setError('Please select a classroom');
      return;
    }

    try {
      setIsLoading(true);
      const response = await ApiService.checkInTeacher({
        teacherId: teacher.sc_id || '',
        classroomId: selectedClassroom
      });
      
      if (response.success) {
        setShowDialog(false);
        if (onSignInComplete) {
          onSignInComplete();
        }
      } else {
        setError(response.message || 'Failed to sign in teacher');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in teacher');
      console.error('Sign in error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setShowDialog(false);
    setError('');
  };

  return (
    <span className="smartcare-signin-component">
      <span 
        className="signin-icon-button" 
        title="Click to sign user into Smartcare"
        onClick={handleOpenDialog}
      ></span>
      
      {showDialog && (
        <div className="signin-dialog-overlay">
          <div className="signin-dialog">
            <h3>Sign {teacher.name} into SmartCare</h3>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="dialog-content">
              <label htmlFor="classroom-select">Select Classroom:</label>
              <select 
                id="classroom-select"
                value={selectedClassroom}
                onChange={(e) => setSelectedClassroom(e.target.value)}
                disabled={isLoading}
              >
                {availableClassrooms.length > 0 ? (
                  availableClassrooms.map(classroom => (
                    <option key={classroom.id} value={classroom.id}>
                      {classroom.name}
                    </option>
                  ))
                ) : (
                  <option value="">No classrooms available</option>
                )}
              </select>
            </div>
            
            <div className="dialog-actions">
              <button 
                onClick={handleCancel} 
                className="cancel-button"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                onClick={handleSignIn} 
                className="signin-button"
                disabled={isLoading || !selectedClassroom}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default SmartcareSignInButton;
